import * as campaignsService from '@/services/marketing/campaigns'
import * as recipientsService from '@/services/marketing/recipients'

// import router from '@/router'

const DEFAULT_QUERY = {
  pagination: {
    current: 1,
    pageSize: 25,
    pageSizeOptions: ['10', '25', '35', '45', '100'],
    showQuickJumper: true,
    showSizeChanger: true,
  },
  filters: null,
  sorter: { field: 'created_at', order: 'descend' },
}

const DEFAULT_META = {
  total: 25,
}

const DEFAULT_DATA = {
  data: null,
  loading: false,
  query: Object.assign({}, DEFAULT_QUERY),
  meta: Object.assign({}, DEFAULT_META),
}

const DEFAULT_COMP = {
  visible: false,
  loading: false,
  processing: false,
}

/*********************
 * * STORE OBJECT
 *********************/

export default {
  namespaced: true,
  state: {
    // DATA
    campaign: Object.assign({}, DEFAULT_DATA),
    campaigns: Object.assign({}, DEFAULT_DATA),
    recipient_group: Object.assign({}, DEFAULT_DATA),
    recipient_groups: Object.assign({}, DEFAULT_DATA),
    // UI
    ui: {
      CREATE_CAMPAIGN: Object.assign({}, DEFAULT_COMP),
    },
  },

  getters: {
    // Main data
    data: (state) => (key) => state[key].data,
    loading: (state) => (key) => state[key].loading,
    query: (state) => (key) => state[key].query,
    pagination: (state) => (key) => state[key].query.pagination,
    filters: (state) => (key) => state[key].query.filters,
    sorter: (state) => (key) => state[key].query.sorter,
    meta: (state) => (key) => state[key].meta,
    // Components Data
    UI_VISIBLE: (state) => (key) => state.ui[key].visible,
    UI_LOADING: (state) => (key) => state.ui[key].loading,
    UI_PROCESSING: (state) => (key) => state.ui[key].processing,
  },

  mutations: {
    INIT: (state) => {
      state.campaign = state.campaigns = state.projects = state.tasks = Object.assign({}, DEFAULT_DATA)
      for (const [key, data] of Object.entries(state.ui)) {
        state.ui[key] = Object.assign({}, DEFAULT_COMP)
      }
    },
    UPDATE: (state, { key, data }) => {
      if (state[key]) state[key].data = data
    },
    SET_LOADING: (state, { key, loading }) => {
      if (state[key]) state[key].loading = loading
    },
    START: (state, key) => {
      if (state[key]) state[key].loading = true
    },
    END: (state, key) => {
      if (state[key]) state[key].loading = false
    },
    SET_QUERY: (state, { key, query }) => {
      if (state[key]) state[key].query = query || Object.assign({}, DEFAULT_QUERY)
    },
    SET_PAGINATION: (state, { key, pagination }) => {
      if (state[key]) {
        state[key].query = state[key].query || Object.assign({}, DEFAULT_QUERY)
        state[key].query.pagination = pagination
      }
    },
    SET_FILTERS: (state, { key, filters }) => {
      if (state[key]) {
        state[key].query = state[key].query || Object.assign({}, DEFAULT_QUERY)
        state[key].query.filters = filters
      }
    },
    SET_SORTER: (state, { key, sorter }) => {
      if (state[key]) {
        state[key].query = state[key].query || Object.assign({}, DEFAULT_QUERY)
        state[key].query.sorter = sorter
      }
    },
    SET_META: (state, { key, meta }) => {
      if (state[key]) state[key].meta = meta
    },
    UI_VISIBLE: (state, { key, visible }) => {
      if (state.ui[key]) state.ui[key].visible = visible
    },
    UI_LOADING: (state, { key, loading }) => {
      if (state.ui[key]) state.ui[key].loading = loading
    },
    UI_PROCESSING: (state, { key, processing }) => {
      if (state.ui[key]) state.ui[key].processing = processing
    },
  },
  actions: {
    // initialization
    init({ commit }) {
      commit('INIT')
    },

    // set pagination
    setPagination({ commit }, { key, pagination }) {
      if (pagination && pagination.current) commit('SET_PAGINATION', { key, pagination })
    },
    // set filters
    setFilters({ commit }, { key, filters }) {
      if (filters && Object.keys(filters).length) commit('SET_FILTERS', { key, filters })
    },
    // set sorter
    setSorter({ commit }, { key, sorter }) {
      if (sorter && sorter.field) commit('SET_SORTER', { key, sorter })
    },
    // set all query same time
    updateQuery({ dispatch }, { key, query: { pagination, filters, sorter } }) {
      dispatch('setPagination', { key, pagination })
      dispatch('setFilters', { key, filters })
      dispatch('setSorter', { key, sorter })
      dispatch('fetchAll', key)
    },

    /*********************
     *  CUSTOMERS
     *********************/

    async fetchAll({ dispatch }, key) {
      if (key === 'campaigns') {
        return await dispatch('fetchCampaigns')
      }
      return null
    },

    // navigation handling
    // async navigateToList({ dispatch }) {
    //   dispatch('init')
    //   dispatch('close', 'CREATE_CUSTOMER')
    //   if (router.history.current.path !== '/crm/campaigns') router.push('/crm/campaigns')
    //   dispatch('fetchCampaigns')
    // },

    // data handling
    async fetchCampaigns({ commit, state }) {
      commit('START', 'campaigns')
      const { campaigns: data, meta } = await campaignsService.fetchCampaigns(state.campaigns.query)
      if (data) commit('UPDATE', { key: 'campaigns', data })
      if (meta) commit('SET_META', { key: 'campaigns', meta })
      commit('END', 'campaigns')
      return data
    },

    // fetch campaign by id
    async fetchCampaign({ commit }, id) {
      commit('START', 'campaign')
      if (id) {
        const data = await campaignsService.fetchCampaign(id)
        if (data) commit('UPDATE', { key: 'campaign', data })
        commit('END', 'campaign')
        return data
      }
      commit('END', 'campaign')
    },

    async createCampaign({ commit }, campaign) {
      commit('START', 'campaign')
      if (campaign) {
        const data = await campaignsService.createCampaign(campaign)
        if (data) {
          commit('UPDATE', { key: 'campaign', data })
        }
        commit('END', 'campaign')
        return data
      }
      commit('END', 'campaign')
      return null
    },

    async updateCampaign({ commit }, campaign) {
      commit('START', 'campaign')
      if (campaign) {
        const data = await campaignsService.updateCampaign(campaign)
        if (data) commit('UPDATE', { key: 'campaign', data })
        commit('END', 'campaign')
        return data
      }
      commit('END', 'campaign')
      return null
    },

    /*********************
     *  Recipient Groups
     *********************/

    async fetchRecipientGroups({ commit }) {
      commit('START', 'recipient_groups')
      const recipient_groups = await recipientsService.fetchRecipientGroups()
      if (recipient_groups) commit('UPDATE', { key: 'recipient_groups', data: recipient_groups })
      commit('END', 'recipient_groups')
      return recipient_groups
    },

    // fetch campaign by id
    async fetchRecipientGroup({ commit }, id) {
      commit('START', 'recipient_group')
      if (id) {
        const data = await recipientsService.fetchRecipientGroup(id)
        if (data) commit('UPDATE', { key: 'recipient_group', data })
        commit('END', 'recipient_group')
        return data
      }
      commit('END', 'recipient_group')
    },

    async createRecipientGroup({ commit }, recipient_group) {
      commit('START', 'recipient_group')
      if (recipient_group) {
        const data = await recipientsService.createRecipientGroup(recipient_group)
        if (data) {
          commit('UPDATE', { key: 'recipient_group', data })
        }
        commit('END', 'recipient_group')
        return data
      }
      commit('END', 'recipient_group')
      return null
    },

    async updateRecipientGroup({ commit }, recipient_group) {
      commit('START', 'recipient_group')
      if (recipient_group) {
        const data = await recipientsService.updateRecipientGroup(recipient_group)
        if (data) commit('UPDATE', { key: 'recipient_group', data })
        commit('END', 'recipient_group')
        return data
      }
      commit('END', 'recipient_group')
      return null
    },

    // Components Handling
    /*********************
     *  COMPONENTS
     *********************/
    UI_VISIBLE({ commit }, { key, visible }) {
      commit('UI_VISIBLE', { key, visible })
    },
    open({ commit }, key) {
      commit('UI_VISIBLE', { key, visible: true })
    },
    close({ commit }, key) {
      commit('UI_VISIBLE', { key, visible: false })
    },
  },
}
